import React, { useEffect, useState } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSortableData } from '../hooks/useSortableData';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Package,
  Plus,
  Pencil,
  Trash2,
  Search,
  Upload,
  Sparkles,
  Loader2,
  CheckCircle,
  Image,
  ImagePlus,
  Check,
  Boxes
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CatalogPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    sku: '',
    description: '',
    description_en: '',
    category: '',
    unit_of_measure: 'pz',
    image_url: '',
    tags: [],
    default_price: '',
    // Gestione Stock
    stock_management_enabled: null, // null = eredita da catalogo/azienda
    stock_quantity: 0
  });
  const [saving, setSaving] = useState(false);
  const [enrichingProducts, setEnrichingProducts] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [batchEnriching, setBatchEnriching] = useState(false);
  const [standardPricelist, setStandardPricelist] = useState(null);
  const [companyStockEnabled, setCompanyStockEnabled] = useState(false);
  
  // Stati per ricerca immagini
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [searchingImages, setSearchingImages] = useState(false);
  const [foundImages, setFoundImages] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [imageSearchQuery, setImageSearchQuery] = useState('');

  // Filtra prodotti per ricerca
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );
  
  // Hook per ordinamento tabella
  const { sortedData: sortedProducts, requestSort, getSortIcon, getSortClass } = useSortableData(
    filteredProducts, 
    { key: 'name', direction: 'asc' }
  );

  const unitOptions = [
    { value: 'pz', label: 'Pezzi (pz)' },
    { value: 'kg', label: 'Chilogrammi (kg)' },
    { value: 'lt', label: 'Litri (lt)' },
    { value: 'gr', label: 'Grammi (gr)' },
    { value: 'ct', label: 'Cartoni (ct)' },
    { value: 'cf', label: 'Confezioni (cf)' },
  ];

  useEffect(() => {
    fetchProducts();
    fetchStandardPricelist();
    fetchStockSettings();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStandardPricelist = async () => {
    try {
      const res = await axios.get(`${API}/pricelists/standard`);
      setStandardPricelist(res.data);
    } catch (error) {
      console.error('Error fetching standard pricelist:', error);
    }
  };

  const fetchStockSettings = async () => {
    try {
      const res = await axios.get(`${API}/stock/settings`);
      setCompanyStockEnabled(res.data.stock_management_enabled || false);
    } catch (error) {
      // Stock settings might not be configured
    }
  };

  // Ottiene il prezzo dal listino standard per un prodotto
  const getProductPrice = (productId) => {
    if (!standardPricelist?.items) return '';
    const item = standardPricelist.items.find(i => i.product_id === productId);
    return item ? item.price.toString() : '';
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        name_en: product.name_en || '',
        sku: product.sku || '',
        description: product.description || '',
        description_en: product.description_en || '',
        category: product.category || '',
        unit_of_measure: product.unit_of_measure || 'pz',
        image_url: product.image_url || '',
        tags: product.tags || [],
        default_price: getProductPrice(product.id),
        stock_management_enabled: product.stock_management_enabled,
        stock_quantity: product.stock_quantity || 0
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        name_en: '',
        sku: '',
        description: '',
        description_en: '',
        category: '',
        unit_of_measure: 'pz',
        image_url: '',
        tags: [],
        default_price: '',
        stock_management_enabled: null,
        stock_quantity: 0
      });
    }
    setShowProductDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!formData.name) {
      toast.error('Il nome è obbligatorio');
      return;
    }

    setSaving(true);
    try {
      let productId;
      const productData = {
        name: formData.name,
        name_en: formData.name_en,
        sku: formData.sku,
        description: formData.description,
        description_en: formData.description_en,
        category: formData.category,
        unit_of_measure: formData.unit_of_measure,
        image_url: formData.image_url,
        tags: formData.tags,
        // Gestione Stock
        stock_management_enabled: formData.stock_management_enabled,
        stock_quantity: formData.stock_quantity || 0
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        productId = editingProduct.id;
        toast.success('Prodotto aggiornato');
      } else {
        const res = await axios.post(`${API}/products`, productData);
        productId = res.data.id;
        toast.success('Prodotto creato');
      }

      // Se è stato inserito un prezzo, aggiornalo nel listino standard
      if (formData.default_price && parseFloat(formData.default_price) > 0) {
        try {
          await axios.put(`${API}/pricelists/standard/item/${productId}`, {
            price: parseFloat(formData.default_price)
          });
        } catch (priceError) {
          console.error('Error updating price:', priceError);
          // Non bloccare se il prezzo non viene salvato
        }
      }

      setShowProductDialog(false);
      fetchProducts();
      fetchStandardPricelist();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!await confirmAction({ title: 'Elimina prodotto', description: 'Sei sicuro di voler eliminare questo prodotto?', confirmText: 'Elimina', variant: 'destructive' })) return;

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Prodotto eliminato');
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  const handleEnrichProduct = async (productId) => {
    setEnrichingProducts(prev => ({ ...prev, [productId]: true }));
    try {
      const res = await axios.post(`${API}/ai/enrich-product/${productId}`);
      toast.success(`Prodotto arricchito con AI: ${res.data.enriched_fields.join(', ')}`);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'arricchimento AI"));
    } finally {
      setEnrichingProducts(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleBatchEnrich = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Seleziona almeno un prodotto');
      return;
    }
    
    setBatchEnriching(true);
    try {
      const res = await axios.post(`${API}/ai/enrich-products-batch`, selectedProducts);
      toast.success(`${res.data.success.length} prodotti arricchiti con successo`);
      if (res.data.failed.length > 0) {
        toast.warning(`${res.data.failed.length} prodotti non arricchiti`);
      }
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'arricchimento batch"));
    } finally {
      setBatchEnriching(false);
    }
  };

  // Cerca immagini per il prodotto
  const handleSearchImages = async (customQuery = null) => {
    // Se non c'è una query personalizzata, usa il nome del prodotto
    const queryToUse = customQuery || imageSearchQuery || formData.name;
    
    if (!queryToUse) {
      toast.error('Inserisci prima il nome del prodotto');
      return;
    }
    
    setSearchingImages(true);
    setFoundImages([]);
    setSelectedImageUrl('');
    setShowImageSearch(true);
    
    // Imposta la query nel campo di ricerca se non è già impostata
    if (!imageSearchQuery && !customQuery) {
      const defaultQuery = formData.category 
        ? `${formData.name} ${formData.category}` 
        : formData.name;
      setImageSearchQuery(defaultQuery);
    }
    
    try {
      const res = await axios.get(`${API}/products/search-images`, {
        params: { query: queryToUse, count: 8 }
      });
      
      setFoundImages(res.data.images || []);
      
      if (res.data.fallback) {
        toast.info('Usando immagini placeholder.');
      }
    } catch (error) {
      console.error('Error searching images:', error);
      toast.error('Errore nella ricerca immagini');
    } finally {
      setSearchingImages(false);
    }
  };

  // Ricerca con nuova query
  const handleNewImageSearch = () => {
    if (imageSearchQuery.trim()) {
      handleSearchImages(imageSearchQuery.trim());
    }
  };

  // Gestisce invio con Enter
  const handleImageSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNewImageSearch();
    }
  };

  // Seleziona un'immagine trovata
  const handleSelectImage = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
  };

  // Conferma selezione immagine
  const handleConfirmImage = () => {
    if (selectedImageUrl) {
      setFormData(prev => ({ ...prev, image_url: selectedImageUrl }));
      setShowImageSearch(false);
      setImageSearchQuery(''); // Reset query
      toast.success('Immagine selezionata!');
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === sortedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(sortedProducts.map(p => p.id));
    }
  };

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <ConfirmDialog />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t('catalog.title')}</h1>
          <p className="text-muted-foreground mt-1">
            Gestisci i tuoi prodotti
          </p>
        </div>
        <div className="flex gap-2">
          {selectedProducts.length > 0 && (
            <Button 
              variant="outline"
              onClick={handleBatchEnrich}
              disabled={batchEnriching}
              data-testid="batch-enrich-btn"
              className="border-purple-500/50 text-purple-600 hover:bg-purple-500/10"
            >
              {batchEnriching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Arricchisci {selectedProducts.length} con AI
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={() => navigate('/catalog/import')}
            data-testid="import-catalog-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('catalog.importCatalog')}
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/catalog/import-ai')}
            data-testid="import-catalog-ai-btn"
            className="border-primary/50 hover:bg-primary/10"
          >
            <Sparkles className="mr-2 h-4 w-4 text-primary" />
            Importa con AI
          </Button>
          <Button 
            onClick={() => handleOpenDialog()}
            data-testid="add-product-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('catalog.addProduct')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca prodotti..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="catalog-search"
        />
      </div>

      {/* Products Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : sortedProducts.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input 
                      type="checkbox" 
                      checked={selectedProducts.length === sortedProducts.length && sortedProducts.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-border"
                    />
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs w-16 cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('product_number')}
                  >
                    <span className="flex items-center gap-1">
                      ID
                      <span className={getSortClass('product_number')}>{getSortIcon('product_number')}</span>
                    </span>
                  </TableHead>
                  <TableHead className="uppercase text-xs w-16"></TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('name')}
                  >
                    <span className="flex items-center gap-1">
                      {t('common.name')}
                      <span className={getSortClass('name')}>{getSortIcon('name')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('sku')}
                  >
                    <span className="flex items-center gap-1">
                      SKU
                      <span className={getSortClass('sku')}>{getSortIcon('sku')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('category')}
                  >
                    <span className="flex items-center gap-1">
                      {t('common.category')}
                      <span className={getSortClass('category')}>{getSortIcon('category')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('unit_of_measure')}
                  >
                    <span className="flex items-center gap-1">
                      {t('common.unit')}
                      <span className={getSortClass('unit_of_measure')}>{getSortIcon('unit_of_measure')}</span>
                    </span>
                  </TableHead>
                  <TableHead className="uppercase text-xs text-right">Prezzo</TableHead>
                  <TableHead className="uppercase text-xs">Tags</TableHead>
                  <TableHead className="uppercase text-xs">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProducts.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell>
                      <input 
                        type="checkbox" 
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        className="rounded border-border"
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {product.product_number || '-'}
                    </TableCell>
                    <TableCell>
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        {product.name_en && (
                          <p className="text-xs text-muted-foreground">{product.name_en}</p>
                        )}
                        {product.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{product.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {product.sku || '-'}
                    </TableCell>
                    <TableCell>{product.category || '-'}</TableCell>
                    <TableCell>{product.unit_of_measure}</TableCell>
                    <TableCell className="text-right font-medium">
                      {getProductPrice(product.id) ? (
                        <span>{formatCurrency(parseFloat(getProductPrice(product.id)))}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-32">
                        {product.tags?.slice(0, 3).map((tag, i) => (
                          <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            {tag}
                          </span>
                        ))}
                        {product.tags?.length > 3 && (
                          <span className="text-xs text-muted-foreground">+{product.tags.length - 3}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleEnrichProduct(product.id)}
                          disabled={enrichingProducts[product.id]}
                          title="Arricchisci con AI"
                          data-testid={`enrich-product-${product.id}`}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-500/10"
                        >
                          {enrichingProducts[product.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenDialog(product)}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteProduct(product.id)}
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('catalog.noProducts')}</p>
            <Button 
              className="mt-4"
              onClick={() => handleOpenDialog()}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('catalog.addProduct')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent scrollable className="max-w-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle className="font-heading">
              {editingProduct ? t('catalog.editProduct') : t('catalog.addProduct')}
            </DialogTitle>
            {editingProduct?.product_number && (
              <p className="text-sm text-muted-foreground">
                ID Prodotto: <span className="font-mono font-medium">{editingProduct.product_number}</span>
              </p>
            )}
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('catalog.productName')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_en">{t('catalog.productNameEn')}</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                data-testid="product-name-en-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sku">{t('catalog.sku')}</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                data-testid="product-sku-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">{t('common.category')}</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="es. Ortofrutta, Latticini..."
                data-testid="product-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">{t('catalog.unitOfMeasure')}</Label>
              <Select 
                value={formData.unit_of_measure} 
                onValueChange={(v) => setFormData({ ...formData, unit_of_measure: v })}
              >
                <SelectTrigger data-testid="product-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_price">Prezzo Listino Standard (€)</Label>
              <Input
                id="default_price"
                type="number"
                step="0.01"
                min="0"
                value={formData.default_price}
                onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                placeholder="0.00"
                data-testid="product-price-input"
              />
              <p className="text-xs text-muted-foreground">
                Questo prezzo sarà applicato al listino standard
              </p>
            </div>

            {/* Gestione Stock - Solo se abilitato a livello azienda o prodotto */}
            <div className="space-y-4 md:col-span-2 p-4 rounded-lg bg-muted/30 border">
              <div className="flex items-center gap-2">
                <Boxes className="h-5 w-5 text-muted-foreground" />
                <Label className="text-base font-medium">Gestione Stock</Label>
              </div>
              
              {/* Toggle override prodotto */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Gestione stock per questo prodotto</div>
                  <div className="text-xs text-muted-foreground">
                    {formData.stock_management_enabled === null 
                      ? (companyStockEnabled 
                          ? 'Eredita da impostazioni azienda (attivo)' 
                          : 'Eredita da impostazioni azienda (disattivo)')
                      : (formData.stock_management_enabled 
                          ? 'Attivo per questo prodotto' 
                          : 'Disattivo per questo prodotto')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={formData.stock_management_enabled === null ? 'inherit' : (formData.stock_management_enabled ? 'yes' : 'no')}
                    onValueChange={(value) => {
                      const newValue = value === 'inherit' ? null : value === 'yes';
                      setFormData({ ...formData, stock_management_enabled: newValue });
                    }}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="stock-enabled-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Eredita</SelectItem>
                      <SelectItem value="yes">Attivo</SelectItem>
                      <SelectItem value="no">Disattivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Quantità stock - visibile solo se stock è attivo */}
              {(formData.stock_management_enabled === true || 
                (formData.stock_management_enabled === null && companyStockEnabled)) && (
                <div className="space-y-2">
                  <Label htmlFor="stock_quantity">Quantità Disponibile</Label>
                  <Input
                    id="stock_quantity"
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    data-testid="stock-quantity-input"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">Immagine Prodotto</Label>
              <div className="flex gap-2">
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  className="flex-1"
                  data-testid="product-image-input"
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleSearchImages}
                  disabled={searchingImages || !formData.name}
                  data-testid="search-images-btn"
                >
                  {searchingImages ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {formData.image_url && (
                <div className="mt-2">
                  <img 
                    src={formData.image_url} 
                    alt="Anteprima" 
                    className="w-20 h-20 object-cover rounded border"
                    onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                  />
                </div>
              )}
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">{t('common.description')} (IT)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="product-description-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description_en">{t('common.description')} (EN)</Label>
              <Textarea
                id="description_en"
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                rows={2}
                data-testid="product-description-en-input"
              />
            </div>
          </div>
          </div>
          <DialogFooter className="shrink-0 px-6 py-4 border-t bg-background">
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSaveProduct}
              disabled={saving}
              data-testid="save-product-btn"
            >
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ricerca Immagini */}
      <Dialog open={showImageSearch} onOpenChange={setShowImageSearch}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Scegli un'immagine per "{formData.name}"
            </DialogTitle>
            <DialogDescription>
              Seleziona una delle immagini suggerite o modifica la ricerca
            </DialogDescription>
          </DialogHeader>
          
          {/* Barra di ricerca personalizzata */}
          <div className="flex gap-2 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={imageSearchQuery}
                onChange={(e) => setImageSearchQuery(e.target.value)}
                onKeyPress={handleImageSearchKeyPress}
                placeholder="Modifica ricerca... (es: prosciutto crudo parma)"
                className="pl-9"
                data-testid="image-search-query-input"
              />
            </div>
            <Button 
              onClick={handleNewImageSearch}
              disabled={searchingImages || !imageSearchQuery.trim()}
              data-testid="image-search-submit-btn"
            >
              {searchingImages ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
          
          <div className="py-2">
            {searchingImages ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Ricerca immagini su Google...</p>
              </div>
            ) : foundImages.length > 0 ? (
              <div className="grid grid-cols-4 gap-3">
                {foundImages.filter(img => img.thumbnail && typeof img.thumbnail === 'string' && img.thumbnail.startsWith('http')).map((image) => (
                  <button
                    key={image.id}
                    onClick={() => handleSelectImage(image.regular || image.small || image.thumbnail)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                      selectedImageUrl === (image.regular || image.small || image.thumbnail)
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'border-transparent hover:border-muted-foreground/30'
                    }`}
                  >
                    <img 
                      src={image.thumbnail || image.small} 
                      alt={image.alt || 'Immagine prodotto'}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    {selectedImageUrl === (image.regular || image.small || image.thumbnail) && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <Check className="h-8 w-8 text-white drop-shadow-lg" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent p-1">
                      <p className="text-white text-xs truncate">{image.author || 'Google Images'}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nessuna immagine trovata</p>
                <p className="text-sm">Prova a modificare la ricerca sopra</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImageSearch(false); setImageSearchQuery(''); }}>
              Annulla
            </Button>
            <Button 
              onClick={handleConfirmImage}
              disabled={!selectedImageUrl}
            >
              <Check className="h-4 w-4 mr-2" />
              Usa questa immagine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogPage;
